export enum ConditionOperator {
	GreaterThan,
	GreaterThanEqual,
	LessThan,
	LessThanEqual,
	Equal,
	NotEqual
}

export interface IConditionOperatorType {
	conditionOperator: ConditionOperator;
	persianName: string;
}

export interface IFieldValue {
	field: Field;
	value: any;
}

export enum Field {
	LastTradedPrice,
	LastTradedPriceVarPercent,
	TotalNumberOfSharesTraded,
	BestBuyLimitQuantity_1,
	BestSellLimitQuantity_1,
	ClosingPrice,
	ClosingPriceVarPercent,
	BestBuyLimitPrice_1,
	BestSellLimitPrice_1,
	TotalTradeValue
}

export interface IStockState {
	LastTradedPrice;
	LastTradedPriceVarPercent;
	TotalNumberOfSharesTraded;
	BestBuyLimitQuantity_1;
	BestSellLimitQuantity_1;
	ClosingPrice;
	ClosingPriceVarPercent;
	BestBuyLimitPrice_1;
	BestSellLimitPrice_1;
	TotalTradeValue;
	FeeOfPreviousDaysClosingPrice;
}

export interface IFieldType {
	field: Field;
	persianName: string;
}

export class StockCondition {
	symbolIsin?: string;
	stockIsin?: string;
	id: string;
	name: string;
	simpleConditions: SimpleCondition[] = [];
	notifiedConditionIsSeen = false;
	creationDateTime?: Date;

	constructor(stockIsin: string, id: string, name: string, notifiedConditionIsSeen = false) {
		this.symbolIsin = stockIsin;
		this.stockIsin = stockIsin;
		this.id = id;
		this.name = name;
		this.notifiedConditionIsSeen = notifiedConditionIsSeen;
	}

	isTrue(fieldValues: IFieldValue[]): boolean {
		return this.simpleConditions.every(item => {
			const correspondFieldValue = fieldValues.find(x => x.field === item.fieldValue.field);
			if (!correspondFieldValue) return false;
			if (!item.isTrue(correspondFieldValue)) return false;
			return true;
		});
	}
}

export class SimpleCondition {
	fieldValue: IFieldValue;
	operator: ConditionOperator;
	constructor(fieldValue: IFieldValue, operator: ConditionOperator) {
		this.fieldValue = fieldValue;
		this.operator = operator;
	}
	isTrue(fieldValue: IFieldValue): boolean {
		if (fieldValue.field != this.fieldValue.field) return false;
		switch (this.operator) {
			case ConditionOperator.GreaterThan:
				return +fieldValue.value > +this.fieldValue.value;
			case ConditionOperator.GreaterThanEqual:
				return +fieldValue.value >= +this.fieldValue.value;
			case ConditionOperator.LessThan:
				return +fieldValue.value < +this.fieldValue.value;
			case ConditionOperator.LessThanEqual:
				return +fieldValue.value <= +this.fieldValue.value;
			case ConditionOperator.Equal:
				return +fieldValue.value === +this.fieldValue.value;
			case ConditionOperator.NotEqual:
				return +fieldValue.value !== +this.fieldValue.value;
		}
		return false;
	}
}

export class ConditionOperatorType implements IConditionOperatorType {
	private _conditionOperator: ConditionOperator;
	constructor(conditionOperator: number) {
		this._conditionOperator = conditionOperator;
	}
	public get conditionOperator() {
		return this._conditionOperator;
	}
	public get persianName(): string {
		switch (this.conditionOperator) {
			case ConditionOperator.GreaterThan:
				return 'بزرگتر از';
			case ConditionOperator.GreaterThanEqual:
				return 'بزرگتر یا مساوی';
			case ConditionOperator.LessThan:
				return 'کوچکتر از';
			case ConditionOperator.LessThanEqual:
				return 'کوچکتر یا مساوی';
			case ConditionOperator.Equal:
				return 'مساوی';
			case ConditionOperator.NotEqual:
				return 'مخالف';
			default:
				return 'خطا: عملگر تعریف نشده است';
		}
	}
	public get mathSymbol(): string {
		switch (this.conditionOperator) {
			case ConditionOperator.GreaterThan:
				return '>';
			case ConditionOperator.GreaterThanEqual:
				return '\u2265';
			case ConditionOperator.LessThan:
				return '<';
			case ConditionOperator.LessThanEqual:
				return '\u2264';
			case ConditionOperator.Equal:
				return '=';
			case ConditionOperator.NotEqual:
				return '\u2260';
			default:
				return 'Error! operator not defined.';
		}
	}
	public get description(): string {
		return this.persianName + ' (' + this.mathSymbol + ')';
	}
}

export class FieldType implements IFieldType {
	private _field: Field;

	constructor(field: number) {
		this._field = field;
	}
	public get field() {
		return this._field;
	}
	public get persianName(): string {
		switch (this.field) {
			case Field.LastTradedPrice:
				return 'آخرین قیمت';
			case Field.LastTradedPriceVarPercent:
				return 'درصد تغییر قیمت';
			case Field.TotalNumberOfSharesTraded:
				return 'حجم معاملات';
			case Field.BestBuyLimitQuantity_1:
				return 'حجم تقاضا';
			case Field.BestSellLimitQuantity_1:
				return 'حجم عرضه';
			case Field.ClosingPrice:
				return 'قیمت پایانی';
			case Field.ClosingPriceVarPercent:
				return 'درصد تغییر پایانی';
			case Field.BestBuyLimitPrice_1:
				return 'قیمت تقاضا';
			case Field.BestSellLimitPrice_1:
				return 'قیمت عرضه';
			case Field.TotalTradeValue:
				return 'ارزش معاملات';
			default:
				return 'خطا: آیتم تعریف نشده است';
		}
	}
}

export interface AddStockConditionItemDto {
	isin?: string;
	stockCondition: StockCondition;
}
export interface StockConditionRequest {
	symbolIsin?: string;
	stockIsin?: string;
	id: string;
	name: string;
	rules: Rules[];
	notifiedConditionIsSeen: boolean;
	creationDateTime?: Date;
}
export interface Rules {
	field: Field;
	value: any;
	operator: ConditionOperator;
}
export interface UpdateStockConditionItemDto {
	isin: string;
	stockCondition: StockCondition;
}

export interface BulkAddStockConditionItemsDto {
	// todo: remove it after transforming all local data to server-side
	addStockConditionItemDtos: AddStockConditionItemDto[];
}

export interface UserStockConditionSets {
	id: string;
	creationDateTime: Date;
	stockConditionSets: StockConditionSet[];
}

export interface StockConditionSet {
	isin: string;
	stockConditionItems: StockCondition[];
}

export interface StockConditionSetWithSymbol extends StockConditionSet {
	symbol: string;
	title: string;
}

export interface ConditionalRiskAgreement {
	id: string;
	CreationDateTime: Date;
}

export interface SymbolCondition {
	count: number;
	notifiedCount: number;
	class: string;
}
